'use client';

import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getChildHeadingVariant } from '@/utils/headingLevel';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { MarkDown } from './atoms/MarkDown';

/* ------------------------------ Animation helpers ------------------------------ */

function normalizeAnimation(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'fade',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'fade';
}

function mapGroup(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    if (
        ['ctas', 'ctas-only', 'cta', 'cta-only', 'buttons', 'button'].includes(
            v
        )
    )
        return 'ctas-only';
    if (
        [
            'features',
            'feature',
            'features-only',
            'features only',
            'steps',
            'step',
            'cards',
            'items',
        ].includes(v)
    )
        return 'features';
    return 'text-content';
}

function getElementVariants(preset, duration) {
    const d = Math.max(0.2, Number(duration) || 0.6);
    if (preset === 'slide') {
        return {
            offscreen: { opacity: 0, y: 16 },
            onscreen: {
                opacity: 1,
                y: 0,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    if (preset === 'zoom') {
        return {
            offscreen: { opacity: 0, scale: 0.96 },
            onscreen: {
                opacity: 1,
                scale: 1,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    return {
        offscreen: { opacity: 0 },
        onscreen: { opacity: 1, transition: { type: 'tween', duration: d } },
    };
}

function getStaggerContainer(duration) {
    const d = Math.max(0.2, Number(duration) || 0.6);
    return {
        hidden: { opacity: 1 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: Math.min(0.25, d * 0.35),
                delayChildren: Math.min(0.3, d * 0.4),
            },
        },
    };
}

function MotionMaybe({ enable, k, motionProps, className, children }) {
    if (enable) {
        return (
            <motion.div key={`${k}-on`} className={className} {...motionProps}>
                {children}
            </motion.div>
        );
    }
    return (
        <div
            key={`${k}-off`}
            className={className}
            style={{ opacity: '', transform: '' }}
        >
            {children}
        </div>
    );
}

function unwrapDeep(v) {
    let cur = v;
    const seen = new Set();
    while (cur && typeof cur === 'object' && 'value' in cur && !seen.has(cur)) {
        seen.add(cur);
        cur = cur.value;
    }
    return cur;
}

function valDeep(x, fallback = '') {
    const v = unwrapDeep(x);
    return v == null ? fallback : v;
}

const RIGHT_DIRECTIONAL_ICON =
    /(?:^|\s)fa-(?:arrow|angle|chevron)-(?:right|right-long)(?:\s|$)/;

function getMobileIconRotation(icon, index, stepCount) {
    const isConnector = RIGHT_DIRECTIONAL_ICON.test(String(icon || ''));
    return isConnector && index < stepCount - 1 ? 'rotate-90 sm:rotate-0' : '';
}

export default function Process(props) {
    const {
        Tagline,
        Header,
        Body,
        Steps = [],
        CTA,
        CustomCTALink,
        SecondaryButtonText,
        OutlineButtonText,
        SecondaryButtonLink,
        OutlineButtonLink,
        ColorScheme = 'light',
        VerticalSpacing = 'medium',
        Anchor = 'process',
        Animation = 'stagger',
        Duration = 0.6,
        AnimationGroup = 'features',
        EditorPreview = false,
    } = props;

    const {
        backgroundColor,
        textColor,
        headerColor,
        iconColor,
        thirdButton,
        secondaryButton,
        taglineColor,
        boxColor,
        boxTextColor,
        boxBorder,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);

    const vSpace = getVerticalSpacing(VerticalSpacing);
    const stepHeadingVariant = getChildHeadingVariant(Header);
    const stepTextColor = boxTextColor || textColor;
    const stepIconColor = boxTextColor ? boxTextColor : iconColor;
    const stepBackground = boxColor || '';
    const stepBorder = boxBorder || '';
    const hasAnyCTA = Boolean(CTA || SecondaryButtonText || OutlineButtonText);

    const preset = normalizeAnimation(Animation);
    const unwrappedSteps = unwrapDeep(Steps);
    const rawSteps = Array.isArray(unwrappedSteps)
        ? unwrappedSteps
        : Array.isArray(unwrappedSteps?.value)
        ? unwrappedSteps.value
        : Array.isArray(unwrappedSteps?.items)
        ? unwrappedSteps.items
        : Array.isArray(unwrappedSteps?.value?.value)
        ? unwrappedSteps.value.value
        : [];
    const stepsList = rawSteps.map((step) => {
        const s = unwrapDeep(step) || {};
        return {
            ...s,
            Title: String(valDeep(s.Title, '')),
            Icon: String(valDeep(s.Icon, '')),
            Description: String(valDeep(s.Description, '')),
        };
    });
    const stepsSignature = useMemo(
        () =>
            stepsList
                .map(
                    (s) =>
                        `${s.Title || ''}|${s.Description || ''}|${
                            s.Icon || ''
                        }`
                )
                .join('||'),
        [stepsList]
    );
    const hasSteps = stepsList.length > 0;
    const requestedGroup = mapGroup(
        AnimationGroup || (hasSteps ? 'features' : 'text-content')
    );
    const AnimationGroupFinal =
        requestedGroup === 'ctas-only' && !hasAnyCTA
            ? 'text-content'
            : requestedGroup;
    const animateText = AnimationGroupFinal === 'text-content';
    const animateFeatures = AnimationGroupFinal === 'features';
    const animateCTAs = AnimationGroupFinal === 'ctas-only';

    const [prefersReduced, setPrefersReduced] = useState(false);
    useEffect(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
            const update = (e) => setPrefersReduced(e.matches);
            setPrefersReduced(mq.matches);
            mq.addEventListener?.('change', update);
            return () => mq.removeEventListener?.('change', update);
        }
    }, []);

    const isStagger = preset === 'stagger';
    const enableAnim = !prefersReduced && preset !== 'none';
    // A single-column process can be taller than the mobile viewport. Requiring
    // half of the whole stagger container to be visible means the cascade can
    // never start, leaving every step hidden. Use the same feature-cascade
    // threshold as ItemGrid while retaining the existing threshold elsewhere.
    const viewportAmount = isStagger && animateFeatures ? 0.15 : 0.5;
    const viewport = { once: !EditorPreview, amount: viewportAmount };
    const baseKey = `process-${AnimationGroupFinal}-${preset}-${Duration}-${stepsSignature}-${
        EditorPreview ? 'preview' : 'prod'
    }`;

    const containerMotionProps = useMemo(() => {
        if (!enableAnim || !isStagger) return {};
        const variants = getStaggerContainer(Duration);
        return EditorPreview
            ? { variants, initial: 'hidden', animate: 'show' }
            : { variants, initial: 'hidden', whileInView: 'show', viewport };
    }, [enableAnim, isStagger, Duration, EditorPreview]);

    const childMotionProps = useMemo(() => {
        if (!enableAnim) return {};

        if (isStagger) {
            const childVariants = {
                hidden: { opacity: 0, y: 16 },
                show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: Math.max(0.4, Duration - 0.2) },
                },
            };
            return { variants: childVariants };
        }

        const v = getElementVariants(preset, Duration);
        const hasOffscreen = v && 'offscreen' in v;
        const initialState = hasOffscreen ? 'offscreen' : 'hidden';
        const targetState = hasOffscreen ? 'onscreen' : 'show';

        return EditorPreview
            ? { variants: v, initial: initialState, animate: targetState }
            : {
                  variants: v,
                  initial: initialState,
                  whileInView: targetState,
                  viewport,
              };
    }, [enableAnim, preset, isStagger, Duration, EditorPreview]);

    const { hasAny: hasCTAs, element: CTAs } = getCTA(
        {
            ...props,
            CTA,
            CustomCTALink,
            SecondaryButtonText,
            SecondaryButtonLink,
            OutlineButtonText,
            OutlineButtonLink,
        },
        {
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign: 'justify-center',
            className:
                'relative z-40 mt-10 flex max-w-4xl flex-wrap items-center justify-center gap-4 lg:mt-16',
            buttonsClassName:
                'flex flex-wrap items-center justify-center gap-4',
            enableAnim: false,
        }
    );

    return (
        <section
            id={Anchor}
            className={`editable-component relative px-6 lg:px-8 ${backgroundColor} ${vSpace}`}
            style={backgroundStyle}
            data-json="process"
        >
            {(Header || Tagline || Body) && (
                <MotionMaybe
                    enable={enableAnim && isStagger && animateText}
                    k={`${baseKey}-intro-container`}
                    motionProps={containerMotionProps}
                    className="relative z-40 mx-auto pb-16 sm:max-w-xl md:max-w-full md:px-24 lg:max-w-screen-xl "
                >
                    <div className="mb-6 max-w-xl text-center md:mx-auto md:mb-10 lg:max-w-3xl">
                        {Tagline && (
                            <MotionMaybe
                                enable={enableAnim && animateText}
                                k={`${baseKey}-tagline`}
                                motionProps={childMotionProps}
                            >
                                <MarkDown
                                    className={`font-display text-base uppercase leading-7 tracking-wide ${taglineColor}`}
                                >
                                    {Tagline}
                                </MarkDown>
                            </MotionMaybe>
                        )}
                        {Header && (
                            <div>
                                <MotionMaybe
                                    enable={enableAnim && animateText}
                                    k={`${baseKey}-header`}
                                    motionProps={childMotionProps}
                                >
                                    <MarkDown
                                        variant="h2"
                                        className={`mt-2 font-display text-3xl tracking-tight ${headerColor} sm:text-4xl sm:leading-tight`}
                                    >
                                        {Header}
                                    </MarkDown>
                                </MotionMaybe>
                            </div>
                        )}
                        {Body && (
                            <div>
                                <MotionMaybe
                                    enable={enableAnim && animateText}
                                    k={`${baseKey}-body`}
                                    motionProps={childMotionProps}
                                >
                                    <MarkDown
                                        className={`mt-6 text-lg font-text leading-8 ${textColor}`}
                                    >
                                        {Body}
                                    </MarkDown>
                                </MotionMaybe>
                            </div>
                        )}
                    </div>
                </MotionMaybe>
            )}

            <div className="relative z-40 mx-auto max-w-7xl">
                {/* Process Grid */}
                <div className="mx-auto max-w-3xl lg:max-w-none">
                    <div className="flex justify-center">
                        <MotionMaybe
                            enable={enableAnim && isStagger && animateFeatures}
                            k={`${baseKey}-steps-container`}
                            motionProps={containerMotionProps}
                            className={`mx-auto grid grid-cols-1 gap-8 ${
                                stepsList.length === 3
                                    ? 'sm:grid-cols-3 lg:grid-cols-3'
                                    : stepsList.length === 2
                                    ? 'sm:grid-cols-2 lg:grid-cols-2'
                                    : stepsList.length === 5
                                    ? 'sm:grid-cols-3 lg:grid-cols-3'
                                    : stepsList.length === 6
                                    ? 'sm:grid-cols-3 lg:grid-cols-3'
                                    : 'sm:grid-cols-2 lg:grid-cols-4'
                            }`}
                        >
                            {stepsList.map((Step, index) => (
                                <MotionMaybe
                                    key={`${Step.Title}-${index}`}
                                    enable={enableAnim && animateFeatures}
                                    k={`${baseKey}-step-${index}`}
                                    motionProps={childMotionProps}
                                >
                                    <div
                                        className={`${stepTextColor} ${stepBackground} ${stepBorder} max-w-200 flex h-full flex-col rounded-[min(1.5rem,var(--shape-radius))] p-6`}
                                        style={{
                                            maxWidth: '300px',
                                            margin: '0 auto',
                                        }}
                                    >
                                        <div className="relative mb-2 flex min-h-[56px] justify-between">
                                            <MarkDown
                                                variant={stepHeadingVariant}
                                                className="pr-7 font-display text-2xl leading-7"
                                            >
                                                {Step.Title}
                                            </MarkDown>
                                            <i
                                                className={`${
                                                    Step.Icon
                                                } absolute right-0 top-0 ml-2 w-6 text-2xl ${stepIconColor} ${getMobileIconRotation(
                                                    Step.Icon,
                                                    index,
                                                    stepsList.length
                                                )}`}
                                            />
                                        </div>
                                        <MarkDown
                                            className={`${stepTextColor} flex-auto font-text`}
                                        >
                                            {Step.Description}
                                        </MarkDown>
                                    </div>
                                </MotionMaybe>
                            ))}
                        </MotionMaybe>
                    </div>
                </div>

                {/* CTA */}
                <div className="flex w-full justify-center">
                    {hasCTAs && (
                        <MotionMaybe
                            enable={enableAnim && animateCTAs}
                            k={`${baseKey}-ctas`}
                            motionProps={childMotionProps}
                            className=""
                        >
                            {CTAs}
                        </MotionMaybe>
                    )}
                </div>
            </div>
        </section>
    );
}
